import { useCallback, useState } from 'react';

import useSurveySettings from 'src/hooks/useSurveySettings';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import useFollowUpFilters from 'src/components/followUp/hooks/useFollowUpFilters';

const useFollowUpDownload = (onDownload: () => void) => {
  const [isPending, setIsPending] = useState(false);
  const [selectedDownloadTypes, setSelectedDownloadTypes] = useState<number[]>(
    [],
  );
  const { downloadTypes, yearOverYearComparisonAvailable } =
    useSurveySettings();
  const { dashboardConfig } = useDashboardContext();
  const { poll } = useDownloadManager();
  const followUpFilters = useFollowUpFilters();

  const handleCheckboxChange = useCallback(
    (downloadType: number) => {
      setSelectedDownloadTypes((prevState) =>
        prevState.includes(downloadType)
          ? prevState.filter((id) => id !== downloadType)
          : [...prevState, downloadType],
      );
    },
    [setSelectedDownloadTypes],
  );

  const handleDownloadClick = useCallback(() => {
    const downloadTypes = selectedDownloadTypes.join(',');
    const { dateRange, comparisonDateRange, orgUnit, demographicsFilters } =
      followUpFilters;

    setIsPending(true);

    HttpService.post(
      apiPaths.FOLLOW_UP.TICKETS.DOWNLOAD,
      {},
      {
        orgUnit,
        dashboardId: dashboardConfig?.id ?? undefined,
        downloadTypes,
        comparisonDateFrom: comparisonDateRange?.createdAtFrom,
        comparisonDateTo: comparisonDateRange?.createdAtTo,
        // Same signal as useWidgetQuery: the comparison block reads the comparison survey.
        ...(comparisonDateRange && yearOverYearComparisonAvailable
          ? { timeComparison: true }
          : {}),
        ...dateRange,
        ...demographicsFilters,
      },
    )
      .then(() => {
        onDownload();
        poll();
      })
      .finally(() => setIsPending(false));
  }, [
    followUpFilters,
    selectedDownloadTypes,
    onDownload,
    setIsPending,
    poll,
    yearOverYearComparisonAvailable,
  ]);

  return {
    downloadTypes,
    handleCheckboxChange,
    handleDownloadClick,
    isPending,
    selectedDownloadTypes,
  };
};

export default useFollowUpDownload;
